// PreboardingContext.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCandidateData } from './candidatePreboardingApi';

type TabOption = 'candidate-offer' | 'candidate-details' | 'documents' | 'hiring-experience';

interface CandidatePreboardingContextType {
  selectedOption: TabOption;
  setSelectedOption: (value: TabOption) => void;
  mapId: number | null;
  setMapId: (id: number | null) => void;
  candidateData: CandidateData | null;   // ✅ new state
  setCandidateData: (data: CandidateData | null) => void; // ✅ updater
  fetchCandidateData: () => Promise<void>; // ✅ new function
  

}

interface PreboardingProviderProps {
  children: ReactNode;
}

// Define candidate data type (based on your API response)
interface CandidateData {
  candidateEmail: string;
  candidateName: string;
  candidateNum: number;
  isOfferAccepted: boolean;
  jobNum: number;
  jobTitle: string;
  mapID: number;
  recruiterName: string;
  recruiterNum: number;
  updatedByName: string;
  updatedByNum: number;
  recruiterEmail: string;
  actionDate: string | null;
  isDeclined:boolean;
  declinedDate:string | null;
  isProfileComplete:boolean;
  preboardingStatusKey: string;
  preboardingStatus: string;
  hasDocuments: boolean; // Optional field to indicate if documents are present
  DeclineReason: string | null;
}

const CandidatePreboardingContext = createContext<CandidatePreboardingContextType | undefined>(undefined);

export const CandidatePreboardingProvider: React.FC<PreboardingProviderProps> = ({ children }) => {
  const [selectedOption, setSelectedOption] = useState<TabOption>('candidate-offer');
  const [mapId, setMapId] = useState<number | null>(null);
  const [candidateData, setCandidateData] = useState<CandidateData | null>(null);
  const { tenant } = useParams<{ tenant: string }>();
  
  const fetchCandidateData = async () =>{
    try{
      const isCandidate = sessionStorage.getItem("candidate_session") ? true : false;
      const payLoad = {
        MapID: mapId
      }
      const response = await getCandidateData(tenant, payLoad, isCandidate);
      if(response ){
        const mappedData: CandidateData = {
          candidateEmail: response.CandidateEmail,
          candidateName: response.CandidateName,
          candidateNum: response.CandidateNum,
          isOfferAccepted: response.IsOfferAccepted,
          jobNum: response.JobNum,
          jobTitle: response.JobTitle,
          mapID: response.MapID,
          recruiterName: response.RecruiterName,
          recruiterNum: response.RecruiterNum,
          updatedByName: response.UpdatedByName,
          updatedByNum: response.UpdatedByNum,
          recruiterEmail: response.RecruiterEmail,
          actionDate: response.ActionDate ? response.ActionDate : null,
          isDeclined: response.IsDeclined,
          declinedDate: response.DeclinedDate ? response.DeclinedDate : null,
          isProfileComplete: response.IsProfileComplete,
          preboardingStatusKey: response.PreboardingStatusKey,
          preboardingStatus: response.PreboardingStatus,
          hasDocuments: response.HasDocuments || false,
          DeclineReason: response.DeclineReason ? response.DeclineReason : null
        };
        setCandidateData(mappedData);
      }
    }catch(error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if(mapId !== null) {
      fetchCandidateData();
    }
  },[mapId, tenant]);



  return (
    <CandidatePreboardingContext.Provider value={{  selectedOption, setSelectedOption, mapId, setMapId, candidateData, setCandidateData , fetchCandidateData }}>
      {children}
    </CandidatePreboardingContext.Provider>
  );
};

export const useCandidatePreboarding = (): CandidatePreboardingContextType => {
  const context = useContext(CandidatePreboardingContext);
  if (!context) {
    throw new Error('useCandidatePreboarding must be used within a CandidatePreboardingProvider');
  }
  return context;
};
